import React, { useState } from 'react';
import { Box, Chip, Popover, Typography } from '@mui/material';
import { deriveTrustTier, TRUST_COPY, type ThesisEvidence, type TrustTier } from '../utils/trustCopy';
import { TRUST_CHIP_TEST_IDS } from '../testIds';

/**
 * Spec D3 (AC-D3-7): the four-state per-thesis trust chip ⚪⚫🟢🔴.
 * Render-only — the tier comes from `deriveTrustTier` (node-safe, unit-tested);
 * contradicts outranks confirms by derivation, never by styling.
 *
 * Follows the StatTierChip idiom (outlined small chip, theme colors, evidence
 * one click deep in a Popover). Glyph + text label together — colour and emoji
 * are never the only signal (WCAG).
 */

const TRUST_CHIP_COLOR: Record<TrustTier, 'success' | 'error' | 'default'> = {
  simulated: 'default',
  curated: 'default',
  confirmed: 'success',
  contradicted: 'error',
};

export interface TrustChipProps {
  evidence?: ThesisEvidence | null;
  /** Override the registry testid when a surface renders many chips. */
  testId?: string;
}

export const TrustChip: React.FC<TrustChipProps> = ({ evidence, testId }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const assessment = deriveTrustTier(evidence);

  return (
    <>
      <Chip
        label={`${assessment.glyph} ${assessment.label}`}
        color={TRUST_CHIP_COLOR[assessment.tier]}
        size="small"
        variant="outlined"
        data-testid={testId ?? TRUST_CHIP_TEST_IDS.chip}
        data-trust-tier={assessment.tier}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        sx={{ cursor: 'pointer' }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        data-testid={TRUST_CHIP_TEST_IDS.popover}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {assessment.glyph} {assessment.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {assessment.meaning}
          </Typography>
          {(evidence?.realSignal?.length ?? 0) > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1, pt: 1, borderTop: theme => `1px solid ${theme.palette.divider}` }}
            >
              {evidence!.realSignal!
                .filter(entry => entry?.kind)
                .map(entry => `${entry.kind}${entry.n ? ` (n=${entry.n})` : ''}${entry.source ? ` — ${entry.source}` : ''}`)
                .join('; ')}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export { deriveTrustTier, TRUST_COPY };
export type { ThesisEvidence, TrustTier };
